import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { EMPTY, Observable, catchError, throwError } from 'rxjs';
import { AuthService } from '../services/auth/auth.service';
import { CustomErrorCode } from '../constants/custom-error-code';
import { HttpStatusCodes } from '../constants/http-status-codes';

// Central handler for a tenant being disabled mid-session: any authenticated request then returns
// 403 TenantDisabled, so we notify the user and log them out (once, via AuthService). Login
// (auth/token) redirects to the disabled page itself and refresh (auth/refresh) is handled inside
// AuthService.refreshToken, so both are skipped here.
@Injectable()
export class TenantDisabledInterceptor implements HttpInterceptor {
  private readonly skipUrls: string[] = ['auth/token', 'auth/refresh'];

  constructor(private readonly authService: AuthService) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!request.url.includes('api/')) {
      return next.handle(request);
    }

    return next.handle(request).pipe(
      catchError((err: HttpErrorResponse) => {
        if (this.isTenantDisabled(err) && !this.isSkipped(request.url)) {
          this.authService.handleTenantDisabled();
          // Swallow so individual components don't also surface their own generic error toast.
          return EMPTY;
        }
        return throwError(() => err);
      })
    );
  }

  private isTenantDisabled(err: HttpErrorResponse): boolean {
    return err.status === HttpStatusCodes.FORBIDDEN
      && err.error?.errorCode === CustomErrorCode.TenantDisabled;
  }

  private isSkipped(url: string): boolean {
    return this.skipUrls.some((skipUrl) => url.includes(skipUrl));
  }
}
